import type { Experiment } from 'types';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Send } from 'lucide-react';
import { useClient } from 'hooks/useClient';
import { toast } from 'sonner';

type EmailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  experiment: Experiment;
};

export const EmailDialog = ({ open, onOpenChange, experiment }: EmailDialogProps) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const apiClient = useClient();

  const handleSend = async () => {
    if (!email) {
      toast.error('Email address is required');
      return;
    }

    setSending(true);
    try {
      await apiClient.post(`/experiments/${experiment.id}/send-implementation-email`, {
        recipientEmail: email,
        recipientName: name || undefined,
        message: message || undefined,
      });
      toast.success(`Email sent to ${email}`);
      onOpenChange(false);
      setEmail('');
      setName('');
      setMessage('');
    } catch {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-panel border-line rounded-panel border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lede font-semibold tracking-tight">Email Implementation Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="micro-label">To</Label>
            <Input
              placeholder="name@example.com"
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              className="border-line bg-field h-9 rounded-lg text-body"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="micro-label">Recipient Name (optional)</Label>
            <Input
              placeholder="John Doe"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              className="border-line bg-field h-9 rounded-lg text-body"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="micro-label">Message (optional)</Label>
            <Textarea
              placeholder="Here are the implementation details for..."
              value={message}
              onChange={(ev) => setMessage(ev.target.value)}
              rows={3}
              className="border-line bg-field rounded-lg text-body"
            />
          </div>
          <div className="bg-panel2 border-line rounded-card border p-3">
            <p className="text-body font-medium">{experiment.name}</p>
            <p className="supporting mt-1">A link to the implementation details will be included in the email.</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <button
              type="button"
              className="border-line text-dim hover:text-foreground hover:bg-hover flex h-8 items-center rounded-lg border bg-transparent px-3 text-meta transition-colors duration-150"
            >
              Cancel
            </button>
          </DialogClose>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !email}
            className="bg-cta hover:bg-ctahover flex h-8 items-center gap-1.5 rounded-lg px-3 text-meta font-medium text-white transition-colors duration-150 disabled:opacity-40"
          >
            <Send className="size-3.5" strokeWidth={1.5} />
            {sending ? 'Sending...' : 'Send'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
